import type { AxiosInstance, AxiosRequestConfig } from 'axios';

import axios from 'axios';

import type { CustomErrorInterceptor } from '../../common/api/clients/localApiAxiosErrorInterceptors';
import type { ApiPersonalizationQueryParams } from './searchRequests';

import { appConfig } from '../../../appConfig';
import { GdprCookieVendors, hasAcceptedGdprCookieVendorsSync } from '../../../utility/gdpr';
import axiosClientFactory from '../../common/api/clients/clientFactory';
import { createDefaultException } from '../../common/exceptions/exceptionUtils';
import persistance from '../../common/persistance/persistance';

export const prepareRequestParams = ({
    storeId,
    customerGroups,
    device,
    quickSearch,
    searchService = null,
}: ApiPersonalizationQueryParams) => {
    const requestParams = {
        store: storeId,
        groups: customerGroups && !!customerGroups.length ? customerGroups.join(',') : null,
        device,
        direct: !!quickSearch,
        searchService,
    };

    return requestParams;
};

const directSearchErrorAxiosInterceptor: CustomErrorInterceptor = (error) => {
    if (typeof error === 'string') {
        const defaultException = createDefaultException();
        defaultException.friendlyMessage = error;
        return defaultException;
    }

    return null;
};

const getDirectPersonalizationSearchAxiosClient = (
    withConsentCheck: boolean = true,
): AxiosInstance => {
    const acceptedConsent = hasAcceptedGdprCookieVendorsSync([GdprCookieVendors.Loop54]);

    const axiosRequestConfig: AxiosRequestConfig = {
        baseURL: appConfig.coopSettings.serviceAccess.personalizationApiUrl,
        headers: {
            'Ocp-Apim-Subscription-Key':
                appConfig.coopSettings.serviceAccess.personalizationApiSubscriptionKey,
        },
        params: {
            'api-version': appConfig.coopSettings.serviceAccess.personalizationApiVersion,
        },
    };

    if (withConsentCheck) {
        axiosRequestConfig.transformRequest = [
            (data: { customData?: object }) => {
                const consentedData = {
                    ...data,
                    customData: {
                        ...data?.customData,
                        consent: acceptedConsent,
                    },
                };
                return consentedData;
            },
        ];

        if (axios.defaults.transformRequest) {
            axiosRequestConfig.transformRequest = [
                ...axiosRequestConfig.transformRequest,
                ...(Array.isArray(axios.defaults.transformRequest)
                    ? axios.defaults.transformRequest
                    : [axios.defaults.transformRequest]),
            ];
        }
    }

    const loopUserId = persistance.loop54UserSession.get();

    if (acceptedConsent) {
        if (appConfig.coopUserSettings.userId) {
            if (axiosRequestConfig.headers) {
                // accepted consent and logged in
                axiosRequestConfig.headers['User-Id'] = appConfig.coopUserSettings.userId;
            }
        }
        if (loopUserId) {
            if (axiosRequestConfig.headers) {
                // accepted consent and NOT logged in
                axiosRequestConfig.headers['Loop-User-Id'] = loopUserId;
            }
        }
    }

    const directSearchAxiosClient = axiosClientFactory.createDirectApiClient(axiosRequestConfig, [
        directSearchErrorAxiosInterceptor,
    ]);

    directSearchAxiosClient.interceptors.response.use((response) => {
        if (acceptedConsent) {
            const headerId = response.headers['loop-user-id'];
            if (headerId) {
                persistance.loop54UserSession.set(headerId);
            }
        }

        return response;
    });

    return directSearchAxiosClient;
};

export default getDirectPersonalizationSearchAxiosClient;
